import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Avatar,
  useTheme,
  alpha,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import {
  Poll as PollIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { socket } from '../../../config/SocketApi';

// ============================================
// Timer Display Component
// ============================================
const TimerDisplay = ({ timeRemaining, totalTime }) => {
  const theme = useTheme();
  const progress = totalTime > 0 ? (timeRemaining / totalTime) * 100 : 100;
  const isUrgent = timeRemaining <= 10;
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Chip
          icon={<TimerIcon />}
          label={formatTime(timeRemaining)}
          color={isUrgent ? 'error' : 'primary'}
          sx={{ 
            fontWeight: 'bold',
            animation: isUrgent ? 'pulse 1s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
              '100%': { transform: 'scale(1)' },
            },
          }}
        />
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={progress}
        color={isUrgent ? 'error' : 'primary'}
        sx={{ height: 8, borderRadius: 4 }}
      />
    </Box>
  );
};

// ============================================
// Student Poll View Component
// ============================================
const StudentPollView = ({ poll, onSubmit, hasSubmitted, selectedOptions, onOptionSelect, timeRemaining }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  if (!poll) return null;
  
  const isMultiple = poll.is_multi;
  const hasTImer = poll.timer && poll.timer > 0;
  
  return (
    <Card 
      elevation={4}
      sx={{ 
        border: `2px solid ${theme.palette.primary.main}`,
        animation: 'pulse 2s infinite',
        '@keyframes pulse': {
          '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.4)}` },
          '70%': { boxShadow: `0 0 0 10px ${alpha(theme.palette.primary.main, 0)}` },
          '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` },
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <PollIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {t('liveQuiz') || 'Live Quiz'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isMultiple ? (t('selectMultiple') || 'Select multiple answers') : (t('selectOne') || 'Select one answer')}
            </Typography>
          </Box>
        </Box>
        
        {/* Timer Display */}
        {hasTImer && timeRemaining > 0 && (
          <TimerDisplay timeRemaining={timeRemaining} totalTime={poll.timer} />
        )}
        
        <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
          {poll.question}
        </Typography>
        
        {hasSubmitted ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body1" color="success.main" fontWeight={500}>
              {t('answerSubmitted') || 'Answer Submitted!'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('waitingForResults') || 'Waiting for results...'}
            </Typography>
          </Box>
        ) : (
          <>
            {isMultiple ? (
              <FormGroup>
                {poll.options?.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    control={
                      <Checkbox
                        checked={selectedOptions.includes(index)}
                        onChange={() => onOptionSelect(index)}
                        color="primary"
                      />
                    }
                    label={option.option || option.text || option}
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: selectedOptions.includes(index) 
                        ? alpha(theme.palette.primary.main, 0.1) 
                        : 'transparent',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  />
                ))}
              </FormGroup>
            ) : (
              <RadioGroup
                value={selectedOptions[0] ?? ''}
                onChange={(e) => onOptionSelect(parseInt(e.target.value))}
              >
                {poll.options?.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={index}
                    control={<Radio color="primary" />}
                    label={option.option || option.text || option}
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: selectedOptions[0] === index 
                        ? alpha(theme.palette.primary.main, 0.1) 
                        : 'transparent',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            )}
            
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<SendIcon />}
              onClick={onSubmit}
              disabled={selectedOptions.length === 0}
              sx={{ mt: 2 }}
            >
              {t('submitAnswer') || 'Submit Answer'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================
// Main Student Poll Component
// ============================================
const StudentPoll = () => {
  const { t } = useTranslation();
  const [activePoll, setActivePoll] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const timerRef = useRef(null);
  
  // Timer countdown effect
  useEffect(() => {
    if (activePoll?.timer && activePoll.timer > 0 && !hasSubmitted) {
      setTimeRemaining(activePoll.timer);
      
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            // Auto-submit if time runs out and user has selected something
            if (selectedOptions.length > 0 && !hasSubmitted) {
              handleSubmitAnswer();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [activePoll?.id, hasSubmitted]);
  
  // Socket event handlers
  useEffect(() => {
    console.log('[StudentPoll] Setting up socket listeners');
    
    // Receive poll from teacher
    const handlePollReceived = (poll) => {
      console.log('[StudentPoll] Received poll:', poll);
      setActivePoll(poll);
      setHasSubmitted(false);
      setSelectedOptions([]);
      setSubmitError('');
      if (poll.timer) {
        setTimeRemaining(poll.timer);
      }
    };
    
    // Poll ended by teacher
    const handlePollEnded = (results) => {
      console.log('[StudentPoll] Poll ended:', results);
      if (timerRef.current) clearInterval(timerRef.current);
      setActivePoll(null);
      setHasSubmitted(false);
      setSelectedOptions([]);
      setTimeRemaining(0);
      setSubmitError('');
    };
    
    // Timer sync from server
    const handleTimerSync = (data) => {
      console.log('[StudentPoll] Timer sync:', data.timeRemaining);
      setTimeRemaining(data.timeRemaining);
    };
    
    socket.on('poll-question', handlePollReceived);
    socket.on('poll-ended', handlePollEnded);
    socket.on('poll-timer-sync', handleTimerSync);
    
    return () => {
      socket.off('poll-question', handlePollReceived);
      socket.off('poll-ended', handlePollEnded);
      socket.off('poll-timer-sync', handleTimerSync);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
  
  // Select option handler
  const handleOptionSelect = useCallback((optionIndex) => {
    if (hasSubmitted) return;
    
    if (activePoll?.is_multi) {
      setSelectedOptions(prev => {
        if (prev.includes(optionIndex)) {
          return prev.filter(i => i !== optionIndex);
        }
        return [...prev, optionIndex];
      });
    } else {
      setSelectedOptions([optionIndex]);
    }
  }, [activePoll, hasSubmitted]);
  
  // Submit answer handler
  const handleSubmitAnswer = useCallback(() => {
    if (selectedOptions.length === 0 || !activePoll || hasSubmitted) return;
    
    const response = {
      pollId: activePoll.id,
      odooQuizId: activePoll.odoo_quiz_id, // For Odoo storage
      socketId: socket.id,
      selectedOptions,
      submittedAt: new Date().toISOString(),
    };

    console.log('[StudentPoll] Submitting response:', response);
    setSubmitError('');
    socket.timeout(5000).emit('submit-poll-response', response, (error, acknowledgement) => {
      if (error || acknowledgement?.status !== 'success') {
        setHasSubmitted(false);
        setSubmitError(acknowledgement?.message || t('pollSubmitFailed') || 'Could not submit your answer. Please try again.');
        return;
      }
      setHasSubmitted(true);
    });
  }, [selectedOptions, activePoll, hasSubmitted, t]);
  
  // Don't render if no active poll
  if (!activePoll) {
    return null;
  }
  
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 300, // Position to the left of the right panel
        width: 380,
        zIndex: 1300,
        // Mobile responsive
        '@media (max-width: 768px)': {
          right: 16,
          left: 16,
          bottom: 80, // Above the FAB buttons
          width: 'auto',
          maxHeight: '70vh',
          overflow: 'auto',
        },
      }}
    >
      {submitError && <Alert severity="error" sx={{ mb: 1 }}>{submitError}</Alert>}
      <StudentPollView
        poll={activePoll}
        hasSubmitted={hasSubmitted}
        selectedOptions={selectedOptions}
        onOptionSelect={handleOptionSelect}
        onSubmit={handleSubmitAnswer}
        timeRemaining={timeRemaining}
      />
    </Box>
  );
};

export default memo(StudentPoll);
